import {
  Container,
  Heading,
  HStack,
  Stack,
  Grid,
  GridItem,
  Tooltip,
  IconButton,
  Icon,
  Box,
  Text,
} from '@chakra-ui/react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  useEffect, useRef,
  useState,
} from 'react';
import MainLayout from '@src/components/layouts/main/MainLayout';
import MapWidget from '@src/modules/performance/components/MapWidget';
import { FiInfo, FiMapPin } from 'react-icons/fi';
import MoreInfoDrawer from '@src/components/shared/MoreInfoDrawer';
import StatsWidget from '../../components/dashboard-widgets/StatsWidget';
import {
  selectedTemporaryRegionRoadTypeState,
  showMapState,
} from '../../state';
import PrintablePdf from '../../components/PrintablePdf';

import RegionRoadTypeSelector from '../../components/RegionRoadTypeSelector';
import DataDisclaimer from '../../components/DataDisclaimer';
import CustomReportsView from '../../components/CustomReportsView';
import { useCustomReport } from '../../context/custom-reports-api-hooks';
import ReportListModal from '../../components/ReportListModal';

export default function Dashboard() {
  const { data: customReport } = useCustomReport();
  const printableRef = useRef();
  const [showMap, setShowMap] = useRecoilState(showMapState);

  // Reset the temporary region road type state when the dashboard is loaded
  const setTemporaryRegionRoadTypeState = useSetRecoilState(selectedTemporaryRegionRoadTypeState);
  useEffect(
    () => {
      setTemporaryRegionRoadTypeState(null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const subTitle = customReport ? customReport.name : '';

  // Calculate the height of the header to adjust the map widget height
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef(null);

  useEffect(() => {
    const headerEl = headerRef.current;
    if (!headerEl) return;

    // Initial height
    setHeaderHeight(headerEl.offsetHeight);

    // Create ResizeObserver
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === headerEl) {
          setHeaderHeight(entry.contentRect.height);
        }
      });
    });

    resizeObserver.observe(headerEl);

    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <MainLayout
      backgroundColor="gray.50"
      sx={{
        '@media print': {
          backgroundColor: 'white',
        },
      }}
    >
      <Container
        maxW="full"
        flexDirection="column"
        display="flex"
        height="100%"
        flexGrow={1}
        position="relative"
        sx={{
          '@media print': {
            '@page': {
              size: 'landscape',
              margin: '0',
            },
          },
        }}
      >
        <Box ref={headerRef}>
          <Stack
            direction={{ base: 'column', md: 'row' }}
            mb={4}
            width="100%"
            justify="space-between"
            sx={{
              '@media print': {
                display: 'none',
              },
            }}
            className="hidden-print"
          >
            <Heading as="h1">
              Performance Dashboard
            </Heading>
            <HStack>
              <ReportListModal />
              <PrintablePdf
                pageTitle={`Performance Dashboard - ${subTitle}`}
                className="hidden-print"
                fileName={`Performance Dashboard - ${subTitle}`}
                printableRef={printableRef}
                printableStyles={null}
              />
              <Tooltip label="Toggle Map View" placement="bottom">
                <IconButton
                  icon={<Icon as={FiMapPin} boxSize={4} />}
                  aria-label="Toggle Map"
                  borderRadius="full"
                  colorScheme="blue"
                  variant="outline"
                  ml={3}
                  size="md"
                  onClick={() => {
                    setShowMap(!showMap);
                  }}
                />
              </Tooltip>
              <MoreInfoDrawer title="More Information" size="xl">
                <Text mb={4}>
                  The Performance Dashboard presents network-level information relevant to the management of roads in the network by Road Controlling Authorities (RCAs).
                </Text>
                <Text mb={4}>
                  Each tile on the main dashboard represents a specific topic relating to RCA transport management.  Click a tile to go to a detailed page of charts and results that provide more information about the topic.
                </Text>
                <Text mb={4}>
                  You can change the view of the main dashboard by selecting from the dropdown list at the right.
                </Text>
                <Text mb={4}>
                  The “Key Indicators” dashboard provides a targeted view of how RCAs are performing annually across key areas such as road condition, safety, and overall transport programme delivery and service performance.
                </Text>

                <Text mb={4}>
                  The “All Indicators” dashboard provides a comprehensive view of the characteristics, usage and performance of the land transport network, including public transport.
                </Text>

                <Text mb={4}>
                  AMP Evidence dashboards are also available to logged-in users from Approved Organisations (AOs) to be used in accordance with guidance to help the AOs put together bids for funding for the 2027-30 NLTP.
                </Text>
                <Box mt={4} mb={2} border="1px solid" borderColor="gray.100" p={3} borderRadius="md">
                  <Text>
                    <strong>Tip:</strong>
                    {' '}
                    Click the
                    {' '}
                    <Icon as={FiInfo} boxSize={4} />
                    {' '}
                    icon in the page for more detailed descriptions and data sources
                  </Text>
                </Box>
              </MoreInfoDrawer>
            </HStack>
          </Stack>
          <RegionRoadTypeSelector mb={4} />
        </Box>
        <Grid
          templateColumns={showMap ? {
            base: '1fr', md: '1fr 1fr', '2xl': '3fr 2fr',
          } : { base: '1fr' }}
          gap={6}
          transition="all 0.3s ease-in-out"
          position="relative"
          flexGrow={1}
        >
          <GridItem ref={printableRef}>
            <CustomReportsView />
          </GridItem>
          {showMap && (
            <GridItem>
              <MapWidget headerHeight={headerHeight} />
            </GridItem>
          )}
        </Grid>
        <StatsWidget showNetworkCharacteristicButtons />
        <DataDisclaimer />
      </Container>
    </MainLayout>
  );
}
